import { isRunningInNativeApp } from '~/utils/browserInfo';

type PwaEnvironment = {
  isStandalone: boolean;
  isNativeApp: boolean;
  isDesktopWidth: boolean;
};

/**
 * Decide se esta sessão vê a parede de fim do PWA (ENG-843).
 *
 * A assimetria que governa o arquivo: um falso-negativo custa um usuário que
 * não migrou para o app; um falso-positivo cobre o site inteiro com uma parede
 * SEM SAÍDA — inclusive dentro do app nativo, que é uma WebView deste mesmo
 * site. Por isso qualquer dúvida resolve para `false`.
 *
 * NÃO usar `isWebViewUserAgent` (do `useLayout`) nem `isRunningInAppLikeContext()`
 * para isto. Nenhum dos dois detecta PWA: o primeiro testa `wv|webview` na UA
 * (ou seja, o app React Native) e o segundo devolve `true` para qualquer
 * WKWebView, o que inclui o navegador in-app do Instagram e do Facebook. Os
 * dois se chamavam `isPwa`/`isRunningInPWA` até o ENG-843.
 *
 * NÃO usar `isRunningInTWA()` tampouco, nem repetir o teste dele aqui: ele casa
 * `document.referrer` com `android-app://` puro, que o Chrome preenche em
 * QUALQUER navegação iniciada por outro app Android. Ver o comentário de lá.
 */
function readEnvironment(): PwaEnvironment {
  const mediaStandalone = window.matchMedia(
    '(display-mode: standalone)',
  ).matches;

  // `navigator.standalone` é só do Safari iOS e não existe na tipagem padrão.
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
    true;

  return {
    // Estes dois são os ÚNICOS sinais, e eles já cobrem o TWA: uma Trusted Web
    // Activity abre com o `display` do manifest, que era `standalone`. Não
    // existe terceiro sinal por referrer — o `android-app://` pega tráfego de
    // WhatsApp, Gmail e afins, e o pacote não separa nada porque o app nativo
    // herdou o pacote do TWA antigo (`br.bet.apostatudo.twa`). Quem separa app
    // nativo de TWA é o bridge, logo abaixo.
    isStandalone: mediaStandalone || iosStandalone,
    // Único detector confiável do app nativo: o bridge do React Native.
    isNativeApp: isRunningInNativeApp(),
    // Mesmo limiar do `isDesktop` do `useLayout`, mas lido direto de
    // `innerWidth`: a computed de lá é reativa via `useWindowSize`, e com ela
    // a parede apareceria e sumiria enquanto o usuário arrasta a janela do PWA
    // de desktop. Aqui o valor é congelado na criação.
    isDesktopWidth: window.innerWidth >= 1024,
  };
}

export function usePwaShutdown() {
  // O ambiente é lido UMA vez, aqui no setup — nunca em escopo de módulo, que
  // poderia rodar antes de `window.ReactNativeWebView` ser injetado. Os sinais
  // já valem no primeiro frame e não mudam durante a sessão, então o resultado
  // é um booleano simples: nada aqui precisa ser reativo.
  let environment: PwaEnvironment = {
    isStandalone: false,
    isNativeApp: false,
    isDesktopWidth: false,
  };

  if (import.meta.client) {
    try {
      environment = readEnvironment();
    } catch (error) {
      // Fail-safe: sem leitura confiável do ambiente, não há parede.
      console.warn('usePwaShutdown: falha ao ler o ambiente', error);
    }
  }

  const { isStandalone, isNativeApp, isDesktopWidth } = environment;

  return {
    shouldShowWall: isStandalone && !isNativeApp && !isDesktopWidth,
  };
}
